<template>
  <div class="h-form">
    <a-form
      name="form"
      :model="formState"
      :layout="layout"
      :colon="false"
      :label-align="labelAlign"
      autocomplete="off"
    >
      <!-- :label-col="{ style: { width: labelWidth } }" -->
      <a-row :gutter="24">
        <template v-for="(item, i) in formItems" :key="item.field">
          <a-col
            v-show="expand || i < showItemsCount"
            v-bind="formGrid"
            :class="{ 'combile-flex': !!item.children }"
          >
            <a-form-item
              v-bind="validateInfos[item.field]"
              :label-col="{ style: { width: labelWidth } }"
              :style="{ flex: 1 }"
              class="h-form-item"
            >
              <template #label v-if="item.label">
                <span class="form-label">
                  <span class="form-item-title">
                    {{ item.label }}
                    <a-tooltip
                      v-if="item.tip"
                      :title="item.tip"
                      placement="topLeft"
                    >
                      <icon-font class="tip-icon" icon="icon-wenhao" />
                    </a-tooltip>
                  </span>
                  <span class="form-item-subtitle" v-if="item.subTitle">{{
                    item.subTitle
                  }}</span>
                </span>
              </template>
              <!-- 只读情况下显示文本 -->
              <span v-if="readonly || item.readonly">{{ item.value }}</span>
              <!-- 表单组件 -->
              <component
                v-else
                :is="item.type || 'a-input'"
                v-model:value="formState[item.field]"
                v-bind="item.config"
                :getPopupContainer="dropDownTypeCom.includes(item.type as string) ? (trigger: any) => trigger.parentNode : undefined"
              ></component>
            </a-form-item>
            <a-form-item
              v-if="item.children"
              :style="{ width: '22%' }"
              class="form-item-children h-form-item"
            >
              <component
                :is="item.children.type"
                v-model:value="formState[item.children.field]"
                v-bind="item.children.config"
              ></component>
            </a-form-item>
          </a-col>
        </template>
        <a-col class="form-footer">
          <a-form-item class="form-footer-item">
            <div
              class="actions"
              :style="{
                justifyContent: 'flex-end'
              }"
            >
              <div
                class="expand-wrap"
                :class="{ 'is-expand': expand }"
                @click="expand = !expand"
                v-if="showExpand"
              >
                <span class="expand-icon">
                  <icon-font icon="icon-xiangxia"></icon-font>
                </span>
              </div>
              <div class="btn-wrap">
                <a-button type="primary" @click="onSubmit" :loading="loading">
                  <icon-font icon="icon-sousuo" v-show="!loading"></icon-font>
                  搜索
                </a-button>
                <a-button v-if="!hideClearBtn" @click="onReset">
                  <icon-font icon="icon-qingkonghuancun"></icon-font>
                  清空
                </a-button>
              </div>
            </div>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>

    <slot name="tip"></slot>
  </div>
</template>

<script lang="ts" setup>
import {
  PropType,
  ref,
  reactive,
  watch,
  computed,
  toRaw,
  onMounted
} from "vue";
import { Form as AForm } from "ant-design-vue";
// import { LoadingOutlined } from '@ant-design/icons-vue';
import type { FormItemProps } from "@/types/index";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  config: {
    type: Array as PropType<FormItemProps[]>,
    required: true
  },
  labelWidth: {
    type: String,
    default: "80px"
  },
  buttonRight: {
    type: Boolean,
    default: false
  },
  // 表单formitem 响应式栅格布局
  formGrid: {
    type: Object,
    default: () => ({
      xs: 12,
      md: 8,
      xl: 6
    })
  },
  // 是否只读
  readonly: {
    type: Boolean,
    default: false
  },
  // 想要展示几条表单item
  showItemsCount: {
    type: Number,
    default: 8
  },
  // 表单布局
  layout: {
    type: String as PropType<"horizontal" | "inline" | "vertical">,
    default: "horizontal"
  },
  defaultValue: {
    type: Object,
    default: () => ({})
  },
  labelAlign: {
    type: String as PropType<"left" | "right">,
    default: "left"
  },
  loading: {
    type: Boolean,
    default: false
  },
  hideClearBtn: {
    type: Boolean,
    default: false
  }
});
const formItems = ref(props.config);
const emit = defineEmits(["update:modelValue", "confirm", "reset"]);
const expand = ref(false);
const formState = reactive<Record<string, any>>({ ...props.modelValue });
const rules = computed(() => {
  return props.config.reduce((map: any, item) => {
    if (item.rules) {
      map[item.field] = item.rules;
    }
    return map;
  }, {});
});

const { validate, resetFields, validateInfos } = AForm.useForm(
  formState,
  rules
);

watch(formState, (value) => {
  emit("update:modelValue", value);
});

const showExpand = computed(() => {
  return props.config.length > props.showItemsCount;
});

const dropDownTypeCom = ["a-select", "a-cascader", "a-range-picker"];

const setFormSelectOptions = (field: string, value: any[]) => {
  const res = formItems.value.find((item) => item.field === field);
  if (res) {
    res.config.options = value;
  }
};
const onSubmit = async () => {
  return validate()
    .then(() => {
      emit("confirm", toRaw(formState));
    })
    .catch((err) => {
      console.warn("form error", err);
    });
};
const onReset = () => {
  resetFields();
  emit("reset", toRaw(formState));
};

const initFormDefaultValue = () => {
  for (const key in props.defaultValue) {
    if (key in formState) {
      formState[key] = props.defaultValue[key];
    }
  }
};

onMounted(() => {
  initFormDefaultValue();
});

defineExpose({ onSubmit, onReset, setFormSelectOptions });
</script>

<script lang="ts">
export default {
  name: "HForm"
};
</script>

<style lang="less" scoped>
.h-form {
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fff;
  border-radius: 3px;
}
.h-form-item {
  margin-bottom: 16px;
}
.form-label {
  display: inline-flex;
  flex-direction: column;
}
.form-item-title {
  font-size: 13px;
}
.form-item-subtitle {
  font-size: 12px;
  color: #999999;
  // color: rgba(0, 0, 0, 0.25);
}

.combile-flex {
  display: flex;
  gap: 8px;
  :deep(.ant-form-item-control-input-content) {
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .form-item-children {
    :deep(.ant-form-item-control-input) {
      border: 1px solid #d9d9d9;
      border-radius: 5px;
    }

    :deep(.ant-checkbox + span) {
      padding-left: 4px;
      padding-right: 0;
      font-size: 13px;
    }
  }
}
.tip-icon {
  font-size: 12px !important;
  vertical-align: top;
}
.form-footer {
  flex: auto;
  .form-footer-item {
    /* :deep(.ant-form-item-control) {
      justify-content: center;
      align-items: flex-start;
    } */
    height: 100%;
    margin-bottom: 0;
  }
  .actions {
    display: flex;
    max-height: 40px;
    // justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-left: 32px;
    .expand-wrap {
      // min-width: 60px;
      line-height: 32px;
      text-align: right;
      font-size: 10px;
      color: #909399;
      font-weight: 600;
      cursor: pointer;
      .expand-icon {
        display: inline-block;
        transition: all 0.3s ease-out;
        font-size: 12px;
      }
    }
    .is-expand {
      .expand-icon {
        transform: rotate(-180deg);
      }
    }

    .btn-wrap {
      display: inline-flex;
      gap: 24px;
      :deep(.ant-btn-loading-icon) {
        transition: none;
      }
    }
  }
}
:deep(.ant-picker-range) {
  width: 100%;
}
:deep(.ant-picker) {
  width: 100%;
}
</style>
